import { useState, memo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { useShare } from '@2cmstudio/share'
import { Challenge } from '@/types/challenge'
import { useUserChallenges } from '@/features/challenges/UserChallengeProvider'

import { generateShareUrl } from '@/utils/shareTokens'
import { Button } from './Button'
import { ToggleSwitch } from './ToggleSwitch'
import { PauseDurationModal, PauseDuration } from './PauseDurationModal'
import { formatPauseDuration, getTimeUntilResume } from '@/utils/simpleChallengeUtils'

interface ChallengeCardProps {
  challenge: Challenge
  onPause?: (id: string, duration?: PauseDuration) => void
  onResume?: (id: string) => void
  onEdit?: (id: string) => void
  onDelete?: (id: string) => void
}

const ChallengeCard = memo(function ChallengeCard({ 
  challenge, 
  onPause, 
  onResume, 
  onEdit, 
  onDelete
}: ChallengeCardProps) {
  const { challengeRepository } = useUserChallenges()
  const { share } = useShare()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isSharing, setIsSharing] = useState(false)
  const [showPauseModal, setShowPauseModal] = useState(false)

  const generateShare = useMutation({
    mutationFn: async () => {
      // Allow sharing for both authenticated and guest users
      return challengeRepository.generateShareToken(challenge.id)
    },
    onSuccess: async (token) => {
      const shareUrl = generateShareUrl(token)
      await share({
        title: t('sharing.joinMyChallenge', { name: challenge.title }),
        text: t('sharing.workingOnChallenge', { name: challenge.title }),
        url: shareUrl,
      })
      queryClient.invalidateQueries({ queryKey: ['challenges'] })
      toast.success(t('challenges.challengeShared'))
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : t('errors.failedToShareChallenge'))
    },
    onSettled: () => {
      setIsSharing(false)
    }
  })

  const handleShare = () => {
    setIsSharing(true)
    generateShare.mutate()
  }

  const handleToggleChange = (isActive: boolean) => {
    if (isActive) {
      // Resume challenge
      onResume?.(challenge.id)
    } else {
      // Show pause modal
      setShowPauseModal(true)
    }
  }

  const handlePauseConfirm = (duration: PauseDuration) => {
    if (duration.type === 'forever') {
      // For "paused forever", we should delete the challenge
      onDelete?.(challenge.id)
    } else {
      // For other pause durations, use the pause function with duration
      onPause?.(challenge.id, duration)
    }
    setShowPauseModal(false)
  }

  const formatFrequency = (challenge: Challenge) => {
    const { frequency } = challenge
    
    switch (frequency.mode) {
      case 'daily':
        return `${t('challenges.dailyAt')} ${frequency.times?.join(', ') || t('common.notSet')}`
      case 'hourly':
        return `${t('challenges.everyHourFrom')} ${frequency.startHour || 9}:00 ${t('common.to')} ${frequency.endHour || 18}:00`
      case 'weekly':
        const days = [t('time.days.sun'), t('time.days.mon'), t('time.days.tue'), t('time.days.wed'), t('time.days.thu'), t('time.days.fri'), t('time.days.sat')]
        const selectedDays = frequency.daysOfWeek?.map(d => days[d]).join(', ') || t('common.none')
        return `${selectedDays} ${t('common.at')} ${frequency.times?.join(', ') || t('common.notSet')}`
      default:
        return t('challenges.customFrequency')
    }
  }

  // Apply opacity for inactive challenges
  const isInactive = challenge.status !== 'active'
  const containerOpacity = isInactive ? 'opacity-70' : ''

  const handleCardClick = (e: React.MouseEvent) => {
    // Prevent navigation when clicking on buttons or interactive elements
    const target = e.target as HTMLElement
    if (target.closest('button') || target.closest('input') || target.closest('select')) {
      return
    }
    navigate(`/challenges/${challenge.id}`)
  }

  return (
    <div 
      className={`bg-surface p-4 sm:p-xl border-b border-border hover:bg-bg-alt/50 transition-all duration-200 cursor-pointer ${containerOpacity}`}
      onClick={handleCardClick}
    >
      <div className="flex flex-col sm:flex-row sm:items-stretch gap-4 sm:gap-xl mb-4 sm:mb-lg">
        {/* Strike Counter - Prominently displayed on the left */}
        <div className="flex flex-row sm:flex-col items-center justify-center text-center min-w-[90px] sm:min-w-[90px] bg-gradient-to-b from-primary/5 to-primary/10 rounded-xl px-4 sm:px-lg py-3 sm:py-md border border-primary/10">
          <div className="text-3xl sm:text-5xl font-bold text-primary mb-0 sm:mb-2 leading-none mr-3 sm:mr-0">
            {challenge.currentStreak || 0}
          </div>
          <div className="text-xs text-text-muted uppercase tracking-wide font-medium">
            {t('challenges.dayStreak')}
          </div>
          {challenge.bestStreak > 0 && challenge.bestStreak > challenge.currentStreak && (
            <div className="text-xs text-text-faint mt-1 sm:mt-2 bg-surface px-sm py-xs rounded-full ml-2 sm:ml-0">
              {t('common.best')} {challenge.bestStreak}
            </div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-3 sm:mb-md">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg sm:text-xl font-semibold text-text mb-1 sm:mb-sm leading-tight">{challenge.title}</h3>
              {/* Show pause duration if challenge is paused, otherwise show frequency */}
              {challenge.status === 'paused' && challenge.pauseUntil ? (
                <div className="text-sm text-warning bg-warning/15 font-medium">
                  {t('challenges.pausedFor')} {getTimeUntilResume(challenge) || formatPauseDuration(challenge)}
                </div>
              ) : (
                <p className="text-sm text-text-muted font-medium">{formatFrequency(challenge)}</p>
              )}
            </div>
            <div className="flex flex-col items-end gap-sm ml-3 sm:ml-lg">
              <ToggleSwitch
                checked={challenge.status === 'active'}
                onChange={handleToggleChange}
                size="lg"
              />
            </div>
          </div>
          
          {/* Share button */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div className="flex items-center gap-3 sm:gap-md">
              {/* Space for future content */}
            </div>

            <Button
              variant="tertiary"
              size="sm"
              onClick={handleShare}
              disabled={isSharing}
              loading={isSharing}
              className="font-medium w-full sm:w-auto"
            >
              {t('challenges.inviteFriends')}
            </Button>
          </div>
        </div>
      </div>

      <div className="flex gap-2 sm:gap-sm">
        {/* Pause/Resume buttons removed - now using toggle switch */}

        {onEdit && (
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => onEdit(challenge.id)}
            className="w-full sm:w-auto"
          >
            {t('common.edit')}
          </Button>
        )}

      </div>

      {/* Pause Duration Modal */}
      <PauseDurationModal
        isOpen={showPauseModal}
        challengeTitle={challenge.title}
        onConfirm={handlePauseConfirm}
        onCancel={() => setShowPauseModal(false)}
      />
    </div>
  )
})

export { ChallengeCard } 